/**
 * Modern AJAX cart drawer (vanilla JS).
 * - Server-rendered via the Section Rendering API (sections/cart-drawer.liquid), no client templating.
 * - Instant open, debounced/optimistic quantity changes, serialized requests (no race conditions).
 * - Takes over the theme's add-to-cart buttons (.product-form__cart-submit, .add-to-cart) and the header cart icon.
 * - Stays in sync with legacy CartJS code paths (quickshop, quickview, upsell) and the `cart:updated` event.
 */
(function () {
  'use strict';
  if (window.cartDrawerUI) return;

  var SECTION_ID = 'cart-drawer';
  var ROOT = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var ADD_SELECTOR = '.product-form__cart-submit, .add-to-cart';
  var BSS_SELECTOR = '.po-atc-btn, [po-btn-atc]';
  var DEBOUNCE_MS = 350;

  var el = null;
  var lastFocus = null;
  var queue = Promise.resolve();
  var renderSeq = 0;
  var timers = {};

  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }

  /* ---------------- Network ---------------- */

  // XHR (not fetch) so product-option apps that patch XMLHttpRequest keep working.
  function request(method, url, body) {
    return new Promise(function (resolve, reject) {
      var xhr = new XMLHttpRequest();
      xhr.open(method, url, true);
      xhr.setRequestHeader('Accept', 'application/json');
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      if (body && !(body instanceof FormData)) {
        xhr.setRequestHeader('Content-Type', 'application/json');
        body = JSON.stringify(body);
      }
      xhr.onload = function () {
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (e) {}
        if (xhr.status >= 200 && xhr.status < 300 && data) return resolve(data);
        var msg = data && (data.description || data.message);
        if (msg && typeof msg === 'object') msg = Object.values(msg).join(' ');
        reject(new Error(msg || 'Error ' + xhr.status));
      };
      xhr.onerror = function () { reject(new Error('Network error')); };
      xhr.send(body || null);
    });
  }

  // Serialize all cart mutations: Shopify's cart can't handle concurrent writes reliably.
  function enqueue(fn) {
    var p = queue.then(fn, fn);
    queue = p.catch(function () {});
    return p;
  }

  /* ---------------- Rendering ---------------- */

  function render(html) {
    if (!html || !el) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.getElementById('CartDrawerContent');
    var current = document.getElementById('CartDrawerContent');
    if (!next || !current) return;

    var list = $('.cd__items', current);
    var scroll = list ? list.scrollTop : 0;
    var focusedKey = null;
    var active = document.activeElement;
    if (active && current.contains(active)) {
      var li = active.closest('.cd-item');
      if (li) focusedKey = li.getAttribute('data-key') + '|' + (active.getAttribute('data-cd-step') || active.className);
    }

    current.replaceWith(next);

    var newList = $('.cd__items', next);
    if (newList) newList.scrollTop = scroll;
    if (focusedKey) {
      var parts = focusedKey.split('|');
      var row = next.querySelector('.cd-item[data-key="' + CSS.escape(parts[0]) + '"]');
      var target = row && (row.querySelector('[data-cd-step="' + parts[1] + '"]') || row.querySelector('.' + parts[1].split(' ')[0]));
      if (target) target.focus({ preventScroll: true });
      else $('.cd__panel', el).focus({ preventScroll: true });
    }

    var count = parseInt(next.getAttribute('data-cart-count'), 10) || 0;
    el.classList.toggle('cd--empty', count === 0);
    updateCount(count);
    convertCurrency();
  }

  function updateCount(count) {
    $$('#CartCount, [data-cart-count-bubble]').forEach(function (node) {
      if (node.textContent !== String(count)) {
        node.textContent = count;
        node.classList.remove('cd-bump');
        void node.offsetWidth;
        node.classList.add('cd-bump');
      }
    });
  }

  function convertCurrency() {
    if (window.theme && theme.mlcurrency && typeof window.currenciesChange === 'function') {
      try { window.currenciesChange('#CartDrawer span.money'); } catch (e) {}
    }
  }

  function renderFrom(data, seq) {
    if (seq !== undefined && seq !== renderSeq) return; // a newer request is in flight
    if (data && data.sections && data.sections[SECTION_ID]) render(data.sections[SECTION_ID]);
  }

  function refresh() {
    var seq = ++renderSeq;
    return request('GET', ROOT + '?sections=' + SECTION_ID).then(function (data) {
      renderFrom({ sections: data }, seq);
    }).catch(function () {});
  }

  // Keep legacy CartJS (header count via rivets, upsell, free-ship scripts) in sync.
  function syncLegacy(cart) {
    if (!window.CartJS || !CartJS.cart || typeof CartJS.cart.update !== 'function') return;
    var apply = function (c) { try { CartJS.cart.update(c); } catch (e) {} };
    if (cart && cart.items) apply(cart);
    else request('GET', ROOT + 'cart.js').then(apply).catch(function () {});
  }

  /* ---------------- Open / close ---------------- */

  function open() {
    if (!el || el.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('cd-lock');
    var panel = $('.cd__panel', el);
    requestAnimationFrame(function () { panel.focus({ preventScroll: true }); });
  }

  function close() {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('cd-lock');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
  }

  /* ---------------- Add to cart ---------------- */

  function showError(message) {
    var box = document.getElementById('errorDrawer');
    var msg = $('.error-message');
    if (box && msg && window.jQuery) {
      msg.textContent = message;
      jQuery('body').addClass('showOverly');
      jQuery(box).fadeIn(250);
      setTimeout(function () { jQuery('body').removeClass('showOverly'); jQuery(box).fadeOut(200); }, 5000);
    } else {
      window.alert(message);
    }
  }

  function buildFormData(button) {
    var fd;
    if (button.classList.contains('add-to-cart') && button.id) {
      fd = new FormData();
      fd.append('id', button.id);
      fd.append('quantity', button.getAttribute('rel') || 1);
      return fd;
    }
    var form = button.form || button.closest('form');
    if (!form) return null;
    fd = new FormData(form);
    if (!fd.get('id')) return null;
    if (!fd.get('quantity')) fd.set('quantity', 1);
    // Option apps sometimes render inputs outside the product form; the old theme picked those up too.
    var scope = form.closest('.shopify-section, .shopWrapper, .product-single') || document;
    $$('[name^="properties["], [name="selling_plan"]', scope).forEach(function (input) {
      if (input.form === form || !input.name || input.disabled) return;
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
      if (!fd.has(input.name) && input.value !== '') fd.append(input.name, input.value);
    });
    return fd;
  }

  function add(button) {
    var fd = buildFormData(button);
    if (!fd) return false;
    fd.append('sections', SECTION_ID);

    button.classList.add('cd-btn-loading');
    button.setAttribute('aria-busy', 'true');

    var seq = ++renderSeq;
    enqueue(function () { return request('POST', ROOT + 'cart/add.js', fd); })
      .then(function (data) {
        renderFrom(data, seq);
        if (seq !== renderSeq) refresh();
        open();
        syncLegacy();
        document.dispatchEvent(new CustomEvent('cart-drawer:added', { detail: data }));
      })
      .catch(function (err) { showError(err.message); })
      .then(function () {
        button.classList.remove('cd-btn-loading');
        button.removeAttribute('aria-busy');
      });
    return true;
  }

  /* ---------------- Quantity / remove ---------------- */

  function change(key, quantity, row) {
    if (row) {
      row.classList.add('is-loading');
      if (quantity === 0) row.classList.add('is-removing');
    }
    el.classList.add('is-busy');
    var seq = ++renderSeq;
    return enqueue(function () {
      return request('POST', ROOT + 'cart/change.js', {
        id: key,
        quantity: quantity,
        sections: SECTION_ID
      });
    }).then(function (cart) {
      renderFrom(cart, seq);
      if (seq === renderSeq) el.classList.remove('is-busy');
      syncLegacy(cart);
      var item = cart.items && cart.items.filter(function (i) { return i.key === key; })[0];
      if (item && quantity > item.quantity) showLineError(key, item.quantity);
    }).catch(function (err) {
      el.classList.remove('is-busy');
      refresh().then(function () { showLineError(key, null, err.message); });
    });
  }

  function showLineError(key, available, message) {
    var row = el.querySelector('.cd-item[data-key="' + CSS.escape(key) + '"]');
    var box = row && $('.cd-item__error', row);
    if (!box) return;
    box.textContent = message || ('Only ' + available + ' available.');
    box.hidden = false;
  }

  function scheduleChange(row, quantity) {
    var key = row.getAttribute('data-key');
    var input = $('.cd-qty__input', row);
    quantity = Math.max(0, parseInt(quantity, 10) || 0);
    if (input) input.value = quantity;
    var err = $('.cd-item__error', row);
    if (err) err.hidden = true;
    clearTimeout(timers[key]);
    renderSeq++; // the user is editing: don't let in-flight responses overwrite the optimistic value
    el.classList.add('is-busy');
    if (quantity === 0) return change(key, 0, row);
    timers[key] = setTimeout(function () { change(key, quantity, row); }, DEBOUNCE_MS);
  }

  /* ---------------- Discount codes ---------------- */

  function currentCodes() {
    var box = $('[data-cd-discount]', el);
    var raw = box ? box.getAttribute('data-codes') : '';
    return raw ? raw.split(',').filter(Boolean) : [];
  }

  // Cart AJAX API: `discount` replaces the cart's discount codes with this comma-separated list.
  function setCodes(codes) {
    el.classList.add('is-busy');
    var seq = ++renderSeq;
    return enqueue(function () {
      return request('POST', ROOT + 'cart/update.js', { discount: codes.join(','), sections: SECTION_ID });
    }).then(function (cart) {
      renderFrom(cart, seq);
      if (seq === renderSeq) el.classList.remove('is-busy');
      syncLegacy(cart);
      return cart;
    }, function (err) {
      el.classList.remove('is-busy');
      throw err;
    });
  }

  function showDiscountError(message) {
    var box = $('.cd-discount__error', el);
    if (!box) return;
    box.textContent = message;
    box.hidden = !message;
  }

  function applyDiscount(form) {
    var input = $('.cd-discount__input', form);
    var btn = $('.cd-discount__apply', form);
    var code = (input.value || '').trim();
    if (!code) { input.focus(); return; }
    var previous = currentCodes();
    var lower = code.toLowerCase();
    if (previous.some(function (c) { return c.toLowerCase() === lower; })) { input.value = ''; return; }

    var errEl = $('.cd-discount__error', el);
    var template = errEl ? errEl.getAttribute('data-msg-invalid') : '';
    showDiscountError('');
    btn.classList.add('cd-btn-loading');
    btn.disabled = true;

    setCodes(previous.concat(code)).then(function (cart) {
      var hit = (cart.discount_codes || []).filter(function (d) { return d.code.toLowerCase() === lower; })[0];
      if (hit && hit.applicable) return;
      // Invalid or not applicable: drop it again so it doesn't linger on the cart.
      return setCodes(previous).then(function () {
        var newInput = $('.cd-discount__input', el);
        if (newInput) { newInput.value = code; newInput.focus(); }
        showDiscountError((template || 'Code "[code]" is not valid.').replace('[code]', code));
      });
    }).catch(function (err) {
      showDiscountError(err.message);
    }).then(function () {
      btn.classList.remove('cd-btn-loading');
      btn.disabled = false;
    });
  }

  // Public: apply a code from outside the drawer (e.g. the product page promo block).
  // Reads the live cart so existing codes are kept; the code stays on the cart even if no items qualify yet.
  function applyCode(code) {
    var lower = String(code).toLowerCase();
    return request('GET', ROOT + 'cart.js').then(function (cart) {
      var codes = (cart.discount_codes || []).filter(function (d) { return d.applicable; }).map(function (d) { return d.code; });
      if (codes.some(function (c) { return c.toLowerCase() === lower; })) return cart;
      return setCodes(codes.concat(code));
    });
  }

  function removeDiscount(code) {
    setCodes(currentCodes().filter(function (c) { return c !== code; })).catch(function (err) { showDiscountError(err.message); });
  }

  /* ---------------- Events ---------------- */

  function closestFromEvent(e, selector) {
    return e.target && e.target.closest ? e.target.closest(selector) : null;
  }

  function bind() {
    // Capture phase on window runs before the theme's jQuery handlers, so we can take over cleanly.
    window.addEventListener('click', function (e) {
      if (closestFromEvent(e, '.header-cart')) {
        e.preventDefault();
        e.stopPropagation();
        open();
        return;
      }
      if (!window.theme || !theme.ajax_cart) return;
      var btn = closestFromEvent(e, ADD_SELECTOR);
      if (!btn || btn.disabled || btn.getAttribute('disabled') === 'disabled' || btn.closest('#CartDrawer')) return;
      // Product Options by BSS (OPTIS) replaces the button with its own clone and adds to cart itself
      // (validation, line item properties, price add-ons). Let it run; see bssFixSupportCustomAfterAddToCart below.
      if (btn.matches(BSS_SELECTOR)) return;
      if (add(btn)) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);

    // The theme also adds to cart on touchend; block it so mobile doesn't add twice (our click handler runs instead).
    window.addEventListener('touchend', function (e) {
      if (window.theme && theme.ajax_cart && closestFromEvent(e, ADD_SELECTOR)) e.stopPropagation();
      if (closestFromEvent(e, '.header-cart')) e.stopPropagation();
    }, true);

    el.addEventListener('click', function (e) {
      if (closestFromEvent(e, '[data-cd-close]')) {
        var link = closestFromEvent(e, 'a[data-cd-close]');
        if (link && window.location.pathname.indexOf('/collections') === 0) e.preventDefault();
        else if (link) return;
        e.preventDefault();
        close();
        return;
      }
      var removeCode = closestFromEvent(e, '[data-cd-discount-remove]');
      if (removeCode) {
        e.preventDefault();
        removeDiscount(removeCode.getAttribute('data-cd-discount-remove'));
        return;
      }
      var row = closestFromEvent(e, '.cd-item');
      if (!row) return;
      if (closestFromEvent(e, '[data-cd-remove]')) {
        e.preventDefault();
        scheduleChange(row, 0);
        return;
      }
      var step = closestFromEvent(e, '[data-cd-step]');
      if (step) {
        e.preventDefault();
        var input = $('.cd-qty__input', row);
        scheduleChange(row, (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-cd-step'), 10));
      }
    });

    el.addEventListener('change', function (e) {
      if (e.target.matches('.cd-qty__input')) {
        scheduleChange(e.target.closest('.cd-item'), e.target.value);
      } else if (e.target.matches('[data-cd-terms]')) {
        var btn = $('.cd__checkout', el);
        if (btn) btn.disabled = !e.target.checked;
      }
    });

    el.addEventListener('submit', function (e) {
      if (e.target.matches('[data-cd-discount-form]')) {
        e.preventDefault();
        applyDiscount(e.target);
      }
    });

    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' && e.target.matches('.cd-qty__input')) {
        e.preventDefault();
        e.target.blur();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') close();
      if (e.key === 'Tab' && el.classList.contains('is-open')) trapFocus(e);
    });

    // Legacy scripts open the drawer by adding `.active` to `.block-cart`; translate that into open().
    new MutationObserver(function () {
      if (el.classList.contains('active')) {
        el.classList.remove('active');
        open();
      }
    }).observe(el, { attributes: true, attributeFilter: ['class'] });

    // Other code paths that change the cart.
    document.addEventListener('cart:updated', function () { refresh().then(open); syncLegacy(); });
    document.addEventListener('cart:refresh', function () { refresh(); });
    if (window.jQuery) {
      jQuery(document).on('cart.requestComplete', function () { refresh(); });
    }

    // Back/forward cache can show a stale cart.
    window.addEventListener('pageshow', function (e) { if (e.persisted) refresh(); });
  }

  function trapFocus(e) {
    var nodes = $$('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])', el)
      .filter(function (n) { return n.offsetParent !== null; });
    if (!nodes.length) return;
    var first = nodes[0];
    var last = nodes[nodes.length - 1];
    if (e.shiftKey && (document.activeElement === first || !el.contains(document.activeElement))) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function init() {
    el = document.getElementById('CartDrawer');
    if (!el) return;
    // Move to <body> root so no ancestor transform/overflow can clip the fixed drawer.
    if (el.parentNode !== document.body) document.body.appendChild(el);
    bind();
    convertCurrency();
  }

  window.cartDrawerUI = { open: open, close: close, refresh: refresh, add: add, applyCode: applyCode };

  // Product Options by BSS calls this after its own /cart/add.js succeeds; without it, it redirects to /cart.
  if (typeof window.bssFixSupportCustomAfterAddToCart !== 'function') {
    window.bssFixSupportCustomAfterAddToCart = function (bss, response, button, loading) {
      try { bss.helper.removeLoadingBtnCSS(button, loading); } catch (e) {}
      refresh().then(open);
      syncLegacy();
    };
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
